import { naturalSort } from '@/shared'

/** Tolerance for “exactly used up” bucket checks (float noise). */
const BUCKET_FIT_EPSILON = 1e-4

export function parseServiceHours(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  const str = String(raw ?? '').replace(/[^0-9.-]/g, '')
  if (str === '' || str === '-') return NaN
  const n = parseFloat(str)
  return Number.isFinite(n) ? n : NaN
}

/**
 * Parse currency/hours-style numbers from API, Excel, or masked inputs.
 * Supports accounting negatives: (1,234.56) → -1234.56
 */
export function parseServiceRevenue(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (raw == null) return NaN
  let str = String(raw).trim().replace(/,/g, '')
  let isAccountingNegative = false
  if (/^\(.*\)$/.test(str)) {
    isAccountingNegative = true
    str = str.slice(1, -1).trim()
  }
  str = str.replace(/[^0-9.-]/g, '')
  if (str === '' || str === '-') return NaN
  const n = parseFloat(str)
  if (!Number.isFinite(n)) return NaN
  return isAccountingNegative ? -Math.abs(n) : n
}

function normalizeDocumentKey(documentRaw) {
  if (documentRaw == null || String(documentRaw).trim() === '') return 'contract'
  return String(documentRaw).trim().toLowerCase()
}

/**
 * Part # used for bucket keys: empty → '(no part #)', else trimmed uppercase.
 * Matches spreadsheet import and Mongo `partNumber` setters.
 */
export function normalizePartNumberBucketKey(partNumberRaw) {
  if (partNumberRaw == null || String(partNumberRaw).trim() === '') return '(no part #)'
  return String(partNumberRaw).trim().toUpperCase()
}

function partNumberBucketKeyFromService(service) {
  const raw = service?.partNumber
  if (raw == null) return '(no part #)'
  if (typeof raw === 'object') {
    const nested =
      raw?.partNumber ?? raw?.value ?? raw?.code ?? raw?.servicePartNumber
    if (nested != null && String(nested).trim() !== '') {
      return String(nested).trim().toUpperCase()
    }
    return '(no part #)'
  }
  const s = String(raw).trim()
  if (s === '' || s === '[object Object]') return '(no part #)'
  return s.toUpperCase()
}

export function aggregateServicesByDocumentAndPart(servicesList) {
  if (!Array.isArray(servicesList)) return []
  const map = new Map()
  for (const service of servicesList) {
    if (typeof service !== 'object' || service === null) continue
    const documentKey = normalizeDocumentKey(service.document)
    const partKey = partNumberBucketKeyFromService(service)
    const hoursParsed = parseServiceHours(service.hours)
    const revenueParsed = parseServiceRevenue(service.revenue)
    const hours = Number.isFinite(hoursParsed) ? hoursParsed : 0
    const revenue = Number.isFinite(revenueParsed) ? revenueParsed : 0
    const compositeKey = `${documentKey}\0${partKey}`
    let row = map.get(compositeKey)
    if (!row) {
      row = { document: documentKey, partNumber: partKey, hours: 0, revenue: 0 }
      map.set(compositeKey, row)
    }
    row.hours += hours
    row.revenue += revenue
  }
  const rows = Array.from(map.values())
  naturalSort(rows, 'document', 'partNumber', 'asc')
  return rows
}

export function isNegativeCreditLine(serviceLine) {
  if (typeof serviceLine !== 'object' || serviceLine === null) return false
  const hours = parseServiceHours(serviceLine.hours)
  const revenue = parseServiceRevenue(serviceLine.revenue)
  return Number.isFinite(hours) && Number.isFinite(revenue) && hours <= 0 && revenue < 0
}

function creditTargetMatchesBucket(applyTarget, bucket) {
  if (!applyTarget || !bucket) return false
  if (applyTarget.scope !== bucket.scope) return false
  if (
    String(applyTarget.document ?? '').toLowerCase() !== String(bucket.document ?? '').toLowerCase()
  ) {
    return false
  }
  return String(applyTarget.projectId ?? '') === String(bucket.projectId ?? '')
}

/**
 * Sum hours/revenue from other draft credit lines already assigned to the same bucket and part #.
 * Credit values are negative; adding them to the bucket total yields remaining positive capacity.
 *
 * Always pass `excludeCreditLine` (the row being evaluated) so that line is never counted against
 * itself — relying on localId alone can double-count when localId is missing or not stable.
 */
function sumConsumptionFromAssignedCredits(
  bucket,
  partKey,
  draftCreditLines,
  excludeLocalId,
  excludeCreditLine
) {
  let hoursTotal = 0
  let revenueTotal = 0
  if (!Array.isArray(draftCreditLines)) return { hours: 0, revenue: 0 }
  for (const line of draftCreditLines) {
    if (!isNegativeCreditLine(line)) continue
    if (!line.creditApplyTarget) continue
    if (excludeCreditLine != null && line === excludeCreditLine) continue
    if (excludeLocalId != null && line.localId === excludeLocalId) continue
    if (normalizePartNumberBucketKey(line.partNumber) !== partKey) continue
    if (!creditTargetMatchesBucket(line.creditApplyTarget, bucket)) continue
    const h = parseServiceHours(line.hours)
    const r = parseServiceRevenue(line.revenue)
    if (Number.isFinite(h)) hoursTotal += h
    if (Number.isFinite(r)) revenueTotal += r
  }
  return { hours: hoursTotal, revenue: revenueTotal }
}

function creditLineFitsBucket(remainingHours, remainingRevenue, creditLine) {
  const creditHours = creditLine != null ? parseServiceHours(creditLine.hours) : null
  const creditRevenue = creditLine != null ? parseServiceRevenue(creditLine.revenue) : null
  const isWellFormedCredit =
    Number.isFinite(creditHours) &&
    Number.isFinite(creditRevenue) &&
    creditHours < 0 &&
    creditRevenue < 0

  if (!isWellFormedCredit) {
    return remainingHours > 0 && remainingRevenue > 0
  }
  return (
    remainingHours + creditHours >= -BUCKET_FIT_EPSILON &&
    remainingRevenue + creditRevenue >= -BUCKET_FIT_EPSILON
  )
}

function pushSortedCandidates(candidates) {
  candidates.sort((a, b) => {
    const scopeOrderA = a.scope === 'master' ? 0 : 1
    const scopeOrderB = b.scope === 'master' ? 0 : 1
    if (scopeOrderA !== scopeOrderB) return scopeOrderA - scopeOrderB
    const contractFirstA = String(a.document).toLowerCase() === 'contract' ? 0 : 1
    const contractFirstB = String(b.document).toLowerCase() === 'contract' ? 0 : 1
    if (contractFirstA !== contractFirstB) return contractFirstA - contractFirstB
    const docCmp = String(a.document).localeCompare(String(b.document), undefined, { numeric: true })
    if (docCmp !== 0) return docCmp
    if (a.scope === 'legacy' && b.scope === 'legacy') {
      return String(a.companyProjectNumber || '').localeCompare(String(b.companyProjectNumber || ''), undefined, {
        numeric: true
      })
    }
    return 0
  })
}

/**
 * Buckets on master + legacy CO projects where this part # still has positive capacity after other
 * matched credits, and this line’s negative hours/revenue fit in that remainder.
 *
 * @param {object} [options]
 * @param {Array} [options.draftCreditLines] — draft estimate lines (matched credits consume capacity)
 * @param {string|null} [options.excludeLocalId] — line being evaluated (omit from consumption sum)
 * @param {object} [options.creditLine] — the credit row; hours/revenue used for fit checks
 * @returns {{ candidates: Array, emptyReason:
 *   'no_positive_bucket' | 'exhausted_by_other_credits' | 'credit_exceeds_remaining' | null }}
 */
export function buildCreditAbsorptionDetail(partNumberRaw, masterServices, legacyProjects, options = {}) {
  const { draftCreditLines = [], excludeLocalId = null, creditLine = null } = options

  const partKey = normalizePartNumberBucketKey(partNumberRaw)
  const candidates = []
  let foundGrossBucketWithHoursAndRevenue = false
  let anyBucketHasBothRemainingsPositive = false

  const masterAggregated = aggregateServicesByDocumentAndPart(masterServices)
  for (const row of masterAggregated) {
    if (row.partNumber !== partKey) continue
    if (row.hours <= 0 || row.revenue <= 0) continue
    foundGrossBucketWithHoursAndRevenue = true
    const bucket = { scope: 'master', document: row.document, projectId: null }
    const consumed = sumConsumptionFromAssignedCredits(
      bucket,
      partKey,
      draftCreditLines,
      excludeLocalId,
      creditLine
    )
    const remainingHours = row.hours + consumed.hours
    const remainingRevenue = row.revenue + consumed.revenue
    if (remainingHours > BUCKET_FIT_EPSILON && remainingRevenue > BUCKET_FIT_EPSILON) {
      anyBucketHasBothRemainingsPositive = true
    }
    if (!creditLineFitsBucket(remainingHours, remainingRevenue, creditLine)) continue
    candidates.push({
      scope: 'master',
      document: row.document,
      projectId: null,
      hours: remainingHours,
      revenue: remainingRevenue
    })
  }

  for (const legacyProject of legacyProjects || []) {
    if (!legacyProject || typeof legacyProject !== 'object') continue
    const projectId = legacyProject._id != null ? String(legacyProject._id) : null
    if (!projectId) continue
    const legacyAggregated = aggregateServicesByDocumentAndPart(legacyProject.services || [])
    for (const row of legacyAggregated) {
      if (row.partNumber !== partKey) continue
      if (row.hours <= 0 || row.revenue <= 0) continue
      foundGrossBucketWithHoursAndRevenue = true
      const bucket = { scope: 'legacy', document: row.document, projectId }
      const consumed = sumConsumptionFromAssignedCredits(
        bucket,
        partKey,
        draftCreditLines,
        excludeLocalId,
        creditLine
      )
      const remainingHours = row.hours + consumed.hours
      const remainingRevenue = row.revenue + consumed.revenue
      if (remainingHours > BUCKET_FIT_EPSILON && remainingRevenue > BUCKET_FIT_EPSILON) {
        anyBucketHasBothRemainingsPositive = true
      }
      if (!creditLineFitsBucket(remainingHours, remainingRevenue, creditLine)) continue
      candidates.push({
        scope: 'legacy',
        document: row.document,
        projectId,
        companyProjectNumber: legacyProject.companyProjectNumber,
        hours: remainingHours,
        revenue: remainingRevenue
      })
    }
  }

  pushSortedCandidates(candidates)

  let emptyReason = null
  if (candidates.length === 0) {
    if (!foundGrossBucketWithHoursAndRevenue) {
      emptyReason = 'no_positive_bucket'
    } else if (!anyBucketHasBothRemainingsPositive) {
      emptyReason = 'exhausted_by_other_credits'
    } else {
      emptyReason = 'credit_exceeds_remaining'
    }
  }

  return { candidates, emptyReason }
}

/** Returns only the candidate list; use {@link buildCreditAbsorptionDetail} when you need user-facing empty reasons. */
export function buildCreditAbsorptionCandidates(partNumberRaw, masterServices, legacyProjects, options = {}) {
  return buildCreditAbsorptionDetail(partNumberRaw, masterServices, legacyProjects, options).candidates
}

/**
 * When exactly one absorption bucket exists for a credit line, set creditApplyTarget + creditAllocationAuto.
 * Clears those fields when multiple or zero candidates.
 */
export function applySingleCreditTargetAutoAllocate(draftServiceLines, masterServices, legacyProjects, {
  isCreditLine = isNegativeCreditLine
} = {}) {
  if (!Array.isArray(draftServiceLines)) return

  for (const line of draftServiceLines) {
    if (!isCreditLine(line)) {
      delete line.creditApplyTarget
      delete line.creditAllocationAuto
      continue
    }

    const { candidates } = buildCreditAbsorptionDetail(line.partNumber, masterServices, legacyProjects, {
      draftCreditLines: draftServiceLines,
      excludeLocalId: line.localId,
      creditLine: line
    })

    if (candidates.length === 1) {
      const only = candidates[0]
      if (only.scope === 'master') {
        line.creditApplyTarget = { scope: 'master', document: only.document }
      } else {
        line.creditApplyTarget = { scope: 'legacy', projectId: only.projectId, document: only.document }
      }
      line.creditAllocationAuto = true
    } else {
      delete line.creditApplyTarget
      delete line.creditAllocationAuto
    }
  }
}
